import React, { createContext, useContext, useState, useEffect, useMemo, useCallback } from 'react';

const AuthContext = createContext(null);

const API_URL = process.env.REACT_APP_BACKEND_URL;

export const AuthProvider = ({ children }) => {
  const [admin, setAdmin] = useState(null);
  const [token, setToken] = useState(localStorage.getItem('admin_token'));
  const [loading, setLoading] = useState(true);

  const logout = useCallback(() => {
    localStorage.removeItem('admin_token');
    setToken(null);
    setAdmin(null);
  }, []);

  useEffect(() => {
    const fetchAdminInfo = async () => {
      try {
        const response = await fetch(`${API_URL}/api/admin/me`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (response.ok) {
          const data = await response.json();
          setAdmin(data);
        } else {
          logout();
        }
      } catch (error) {
        if (process.env.NODE_ENV !== 'production') {
          console.error('Error fetching admin info:', error);
        }
        logout();
      } finally {
        setLoading(false);
      }
    };

    if (token) {
      fetchAdminInfo();
    } else {
      setLoading(false);
    }
  }, [token, logout]);

  const login = async (email, password) => {
    try {
      const response = await fetch(`${API_URL}/api/admin/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, password })
      });
      
      // Read response text first
      const text = await response.text();
      
      // Try to parse as JSON
      let data;
      try {
        data = JSON.parse(text);
      } catch (e) {
        throw new Error('Invalid response from server');
      }
      
      if (!response.ok) {
        throw new Error(data.detail || 'Login failed');
      }
      
      localStorage.setItem('admin_token', data.access_token);
      setToken(data.access_token);
      setAdmin(data.admin);
      return data;
    } catch (error) {
      if (process.env.NODE_ENV !== 'production') {
        console.error('Login error:', error);
      }
      throw error;
    }
  };

  const register = async (email, password, name) => {
    try {
      const response = await fetch(`${API_URL}/api/admin/register`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, password, name })
      });
      
      // Read response text first
      const text = await response.text();
      
      // Try to parse as JSON
      let data;
      try {
        data = JSON.parse(text);
      } catch (e) {
        throw new Error('Invalid response from server');
      }
      
      if (!response.ok) {
        throw new Error(data.detail || 'Registration failed');
      }
      
      localStorage.setItem('admin_token', data.access_token);
      setToken(data.access_token);
      setAdmin(data.admin);
      return data;
    } catch (error) {
      if (process.env.NODE_ENV !== 'production') {
        console.error('Register error:', error);
      }
      throw error;
    }
  };

  const value = useMemo(
    () => ({ admin, token, loading, login, register, logout }),
    [admin, token, loading, logout]
  );

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
